import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { useToggle } from "@uidotdev/usehooks";
import { cn } from "cn";
import { capitalize } from "lodash-es";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { BwCheckmarkLine, BwChevronDownLine } from "@/components/ui/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

type FeeWaiverSelectProps = FieldWrapperInjectedProps & {
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  className?: string;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  "data-test"?: string;
};

export default function FeeWaiverSelect({
  className,
  value,
  onChange,
  disabled,
  defaultOpen = false,
  onOpenChange,
  ...props
}: FeeWaiverSelectProps) {
  const [open, toggleOpen] = useToggle(defaultOpen);
  const options = ["Yes", "No"];
  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open);
    toggleOpen(open);
  };
  return (
    <Popover modal={true} open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger disabled={disabled} asChild>
        <div
          role="combobox"
          className={cn(
            /* sorted */
            "flex",
            "items-center justify-between",
            "w-full h-10",
            "px-3 py-2",
            "border border-border-dark-low rounded-lg",
            "aria-invalid:border-border-negative-max aria-invalid:data-[state=open]:border-border-negative-max data-[state=open]:border-border-dark-max data-[state=open]:border-[1.5px]",
            className,
          )}
          {...props}
        >
          <p className="text-text-dark-max text-sm font-medium">
            {value ? "Yes" : "No"}
          </p>

          <BwChevronDownLine
            size={16}
            className="text-text-dark-high shrink-0"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command>
          <CommandList>
            <CommandGroup>
              {options.map((option: string) => (
                <CommandItem
                  value={option}
                  key={option}
                  onSelect={(e) => {
                    onChange(option === "Yes");
                    handleOpenChange(false);
                  }}
                >
                  <p className="grow">{capitalize(option)}</p>
                  <BwCheckmarkLine
                    size={16}
                    className={cn(
                      /* sorted */
                      "shrink-0",
                      (value ? "Yes" : "No") === option
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
